import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Space, Popconfirm, message, Typography, Card, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, FileOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { AdvWithClient } from '../../types/adv';
import { advService } from '../../services/advService';
import AdvForm from './AdvForm';

const { Title } = Typography;

const AdvPage: React.FC = () => {
  const { t } = useTranslation();
  const [advs, setAdvs] = useState<AdvWithClient[]>([]);
  const [loading, setLoading] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const [selectedAdv, setSelectedAdv] = useState<AdvWithClient | undefined>(undefined);

  // Function to fetch all advertisements
  const fetchAdvs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await advService.getAllAdvs();
      setAdvs(data);
    } catch (error) {
      console.error('Error fetching advertisements:', error);
      message.error(t('advertising.advs.notifications.error', { message: (error as Error).message }));
    } finally {
      setLoading(false);
    }
  }, [t]);

  // Load advertisements on component mount
  useEffect(() => {
    fetchAdvs();
  }, [fetchAdvs]);

  // Handle editing an advertisement
  const handleEdit = (adv: AdvWithClient) => {
    setSelectedAdv(adv);
    setFormVisible(true);
  };

  // Handle creating a new advertisement
  const handleCreate = () => {
    setSelectedAdv(undefined);
    setFormVisible(true);
  };

  // Handle deleting an advertisement
  const handleDelete = async (id: number) => {
    try {
      await advService.deleteAdv(id);
      message.success(t('advertising.advs.notifications.delete_success'));
      fetchAdvs();
    } catch (error) {
      console.error('Error deleting advertisement:', error);
      message.error(t('advertising.advs.notifications.error', { message: (error as Error).message }));
    }
  };

  // After successful form submission, refresh the list
  const handleFormSuccess = () => {
    fetchAdvs();
  };

  // Table columns
  const columns = [
    {
      title: t('advertising.advs.list.name'),
      dataIndex: 'adv_name',
      key: 'adv_name',
    },
    {
      title: t('advertising.advs.list.file'),
      dataIndex: 'adv_file',
      key: 'adv_file',
      render: (file: string) => {
        // Extract filename from path (e.g., "storage/adv/filename.mp4" -> "filename.mp4")
        const filename = file.split('/').pop() || '';
        // Use direct backend URL in development to avoid proxy issues
        const baseUrl = process.env.NODE_ENV === 'development' ? 'http://localhost:8080' : '';
        const fileUrl = `${baseUrl}/api/advfiles/${filename}`;
        
        return (
          <Space>
            <FileOutlined />
            <a
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ 
                fontFamily: 'monospace', 
                fontSize: '12px',
                color: '#1890ff',
                textDecoration: 'none'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.textDecoration = 'underline';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.textDecoration = 'none';
              }}
            >
              {file.length > 30 ? `...${file.slice(-30)}` : file}
            </a>
          </Space>
        );
      }
    },
    {
      title: t('advertising.advs.list.client'),
      dataIndex: 'client_name',
      key: 'client_name',
      render: (name: string) => (
        <Tag color="blue">{name}</Tag>
      )
    },
    {
      title: t('advertising.advs.list.created'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text: string) => new Date(text).toLocaleString()
    },
    {
      title: t('advertising.advs.list.updated'),
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (text: string) => new Date(text).toLocaleString()
    },
    {
      title: t('advertising.advs.list.actions'),
      key: 'actions',
      render: (_: any, record: AdvWithClient) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          >
            {t('advertising.advs.actions.edit')}
          </Button>
          <Popconfirm
            title={t('advertising.advs.actions.confirm_delete')}
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} danger size="small">
              {t('advertising.advs.actions.delete')}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card title={t('advertising.advs.list.title')}>
      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div></div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
        >
          {t('advertising.advs.actions.create')}
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={advs}
        rowKey="id"
        loading={loading}
        scroll={{ x: 800 }}
      />

      <AdvForm
        adv={selectedAdv}
        isVisible={formVisible}
        onClose={() => setFormVisible(false)}
        onSuccess={handleFormSuccess}
      />
    </Card>
  );
};

export default AdvPage; 